<template>
	<div class="editor" v-if="planning">
		<div class="editor__header">
			<Icon
				:header="true"
				:icon="planning.Info.icon">
			</Icon>
			<h1 class="editor__header__title">Planning  {{planning.Info.title}}</h1>
		</div>
		<div class="editor__content">
			<form
				class="form"
				@keydown.enter.prevent
				@submit.prevent="savePlanning">
				<div class="form__section" v-if="action === 'create'">
					<div class="form__section__text">
						<h2>Course</h2>
						<p>Pick a course for which you want to create a planning. In a later update, you will be able to add custom courses.</p>
					</div>
					<div class="form__section__content form__section__content--course">
						<div class="form__course">
							<label>School</label>
							<Dropdown
								class="form__schools"
								:brick="false"
								:placeholder="'School'"
								v-model="planning.Info.School"
								:options="schools"
								:labels="schools.map(school => school.name)">
							</Dropdown>
							<label>Program</label>
							<Dropdown
								class="form__programs"
								:brick="false"
								:placeholder="'Program'"
								v-model="planning.Info.Program"
								:options="programs"
								:labels="programs.map(program => program.title)">
							</Dropdown>
							<label>Course</label>
							<Dropdown
								class="form__courses"
								:brick="false"
								:placeholder="'Course'"
								v-model="planning.Info.Course"
								:options="courses"
								:labels="courses.map(course => course.title)">
							</Dropdown>
						</div>
					</div>
				</div>
				<div class="form__section" v-if="planning.Info.Course">
					<div class="form__section__text">
						<h2>Information</h2>
						<p>Provide some basic information about your planning, and decide who will be able to see it.</p>
					</div>
					<div class="form__section__content form__section__content--info">
						<div class="form__info">
							<label>Title</label>
							<input
								class="form__title"
								type="text"
								v-model="planning.Info.title"
								placeholder="Title"/>
							<!-- We hide the icon editor for now
							<input
								class="form__icon"
								type="text"
								v-model="planning.Info.icon"
								placeholder="Icon"/>-->
							<div class="form__info__dates">
								<label>Start</label>
								<DatePicker
									:date="planning.Info.start"
									@setDate="setStart">
								</DatePicker>
								<label>End</label>
								<DatePicker
									:date="planning.Info.end"
									@setDate="setEnd">
								</DatePicker>
							</div>
							<label>Visibility</label>
							<Dropdown
								:placeholder="'Visibility'"
								v-model="planning.shared"
								:options="['public', 'friends', 'private']"
								:labels="['Public', 'Friends only', 'Private']">
							</Dropdown>
						</div>
					</div>
				</div>
				<div v-if="planning.Info.end" class="form__section">
					<div class="form__section__text">
						<h2>Topics (optional)</h2>
						<p>It can be worthwhile to divide your planning into different topics. Think of Chapters, Assignments and Subjects - or a mix of all three!</p>
					</div>
					<div class="form__section__content form__section__content--topics">
						<table class="form__topics">
							<tr
								class="form__topic"
								v-for="(topic, index) in planning.Topics"
								:key="index">
								<td>
									<input class="form__topic__title" type="text" placeholder="Title" v-model="topic.title">
								</td>
								<td class="form__topic__delete">
									<button class="form__topic__delete__button" type="button" @click="deleteTopic(index)">
										<i class="fas fa-trash-alt"></i>
									</button>
								</td>
							</tr>
							<tr>
								<td>
									<input class="form__topic__title" type="text" placeholder="Topic title" v-model="newTopic.title">
								</td>
								<td>
									<button
										class="form__topic__add"
										type="button"
										@click="addTopic()">
										Add
									</button>
								</td>
							</tr>
						</table>
					</div>
				</div>
				<!-- We hide the deadline part until it is final -->
				<div v-if="planning.Info.end && false" class="form__section">
					<div class="form__section__text">
						<h2>Deadlines</h2>
						<p>Deadlines differ from tasks in the sense that they can't be 'completed' - they merely serve as visual reminders of important timepoints in your planning.</p>
					</div>
					<div class="form__section__content form__section__content--deadlines">
						<table class="form__deadlines">
							<tr
								class="form__deadlines"
								v-for="(deadline, index) in planning.Deadlines"
								:key="index">
								<td>
									<input class="form__deadline__title" type="text" placeholder="Deadline" v-model="deadline.title">
								</td>
								<td>
									<DatePicker
										class="form__deadline__date"
										:date="deadline.date"
										@setDate="setDeadlineDate(index, ...arguments)">
									</DatePicker>
								</td>
								<td class="form__deadline__delete">
									<button class="form__deadline__delete__button" type="button" @click="deleteDeadline(index)">
										<i class="fas fa-trash-alt"></i>
									</button>
								</td>
							</tr>
							<tr>
								<td>
									<input class="form__deadline__title" type="text" placeholder="Deadline" v-model="newDeadline.title">
								</td>
								<td>
									<DatePicker
										class="form__deadline__date"
										:placeholder="'Deadline date'"
										:date="newDeadline.date"
										@setDate="setDeadlineDate(-1, ...arguments)">
									</DatePicker>
								</td>
								<td>
									<button
										class="form__deadline__add"
										type="button"
										@click="addDeadline()">
										Add
									</button>
								</td>
							</tr>
						</table>
					</div>
				</div>
				<div v-if="planning.Info.end" class="form__section">
					<div class="form__section__text">
						<h2>Tasks</h2>
						<p>What tasks will you plan to do to tackle this course? A course's syllabus is a good starting point, but you can add onto that by planning to occasionaly review or summarize course content! </p>
					</div>
					<div class="form__section__content form__section__content--tasks">
						<TasksEditor v-if="planning.Info.end"
							class="form__tasks"
							:planning="planning"
							:editorWeeks="editorWeeks"
							@addTask="addTask"
							@addChildTask="addChildTask"
							@deleteTask="deleteTask"
							@deleteChildTask="deleteChildTask">
						</TasksEditor>
					</div>
				</div>
				<button
					v-if="planning.Info.end"
					class="form__save"
					type="submit">
					Save
				</button>
			</form>
		</div>
	</div>
</template>

<script>
import DateService from '@/services/DateService'
import PlanningService from '@/services/PlanningService'
import SchoolService from '@/services/SchoolService'
import DatePicker from '@/components/DatePicker.vue'
import Dropdown from '@/components/Dropdown'
import Icon from '@/components/Icon.vue'
import TasksEditor from '@/components/TasksEditor.vue'
require('@/services/DateFormatService')
export default {
	components: {
		DatePicker,
		Dropdown,
		Icon,
		TasksEditor
	},
	data () {
		return {
			setup: false,
			planning: {
				Info: {
					School: null,
					Program: null,
					Course: null,
					start: '',
					end: ''
				},
				Deadlines: [],
				Topics: [],
				Tasks: [],
				User: {},
				shared: 'public'
			},
			newTopic: {},
			newDeadline: {
				date: ''
			},
			schools: [],
			programs: [],
			courses: [],
			editorWeeks: []
		}
	},
	methods: {
		async getPlanning () {
			// If we are creating a new planning, no need to do anything
			if (this.action === 'create') return
			// Ensure plannings are always refreshed when PlanningEditor is opened
			await this.$store.dispatch('getPlannings')
			const planningId = parseInt(this.$route.params.id)
			// Check if the planning exists amongst local plannings
			const plannings = this.$store.state.plannings
			const index = plannings.findIndex(planning => planning.id === planningId)
			// If the user is the owner, get the planning from local storage
			if (index >= 0) this.planning = JSON.parse(JSON.stringify(plannings[index]))
			else this.$router.push('/plannings')
			// Add Child Task fields
			this.planning.Tasks.forEach(task => { task.newChildTask = {} })
		},
		async savePlanning () {
			// Check if there are any unsaved tasks
			var newTasksEmpty = true
			this.editorWeeks.forEach(editorWeek => {
				if (Object.keys(editorWeek.newTask).length > 0) newTasksEmpty = false
			})
			if (!newTasksEmpty) {
				if (!confirm('There are still unsaved tasks which have not been added yet. Do you want to save regardless?')) return
			}
			if (this.action === 'create') {
				await PlanningService.create(this.planning)
				// Update locally retrieved plannings
				await this.$store.dispatch('getPlannings')
				// After creation, return to planning oversight
				this.$router.push('/plannings')
			}	else {
				await PlanningService.edit(this.planning)
				// Update locally retrieved plannings
				await this.$store.dispatch('getPlannings')
				// After creation, return to planning
				this.$router.push(`/planning/${this.planning.id}`)
			}
		},
		addTopic () {
			const topic = this.newTopic
			if (!topic.title) {
				this.$store.dispatch('openPopup', { title: 'Topic creation', message: 'Please specify a title for the topic you are trying to add.'	})
				return
			}
			this.planning.Topics.push(topic)
			this.newTopic = {}
		},
		deleteTopic (index) {
			if (!confirm('Deleting this topic will also delete all of its tasks. Are you sure?')) return
			// Delete topic from local storage (it's not saved on server yet)
			this.$delete(this.planning.Topics, index)
			// Delete tasks which were in this topic
			this.planning.Tasks = this.planning.Tasks.filter(task => task.topicIndex !== index)
			// Move all topicIndexes which exceed the deleted one
			this.planning.Tasks.forEach(function (task) {
				if (task.topicIndex > index) task.topicIndex--
			})
		},
		addDeadline () {
			const deadline = this.newDeadline
			if (!deadline.title) {
				this.$store.dispatch('openPopup', { title: 'Deadline creation', message: 'Please specify a title for the deadline you are trying to add.'	})
				return
			}
			this.planning.Deadlines.push(deadline)
			this.newDeadline = { date: '' }
		},
		deleteDeadline (index) {
			this.$delete(this.planning.Deadlines, index)
		},
		addTask (weekIndex, date, task) {
			// Create new task object
			const newTask = {
				description: task.description,
				index: this.planning.Tasks.length,
				topicIndex: task.topicIndex >= 0 ? task.topicIndex : -1,
				type: task.type,
				Info: { date },
				newChildTask: {}
			}
			// Add task to local storage
			this.planning.Tasks.push(newTask)
			// Clear task field
			this.editorWeeks[weekIndex].newTask = {}
		},
		deleteTask (id) {
			const index = this.planning.Tasks.findIndex(task => task.id === id)
			// Delete task from local storage (it's not saved on server yet)
			this.$delete(this.planning.Tasks, index)
		},
		addChildTask (parentIndex, childTask) {
			// Retrieve parent task
			const parentTask = this.planning.Tasks[parentIndex]
			// Create new Child Task object
			const newChildTask = {
				description: childTask.description,
				topicIndex: parentTask.topicIndex >= 0 ? parentTask.topicIndex : -1,
				type: parentTask.type,
				Info: { date: parentTask.Info.date },
				parent: parentTask.id
			}
			// Add task to local storage
			if (parentTask.ChildTasks) parentTask.ChildTasks.push(newChildTask)
			else this.$set(parentTask, 'ChildTasks', [ newChildTask ])
			// Clear task field
			this.planning.Tasks[parentIndex].newChildTask = {}
		},
		deleteChildTask (parentIndex, childIndex) {
			this.$delete(this.planning.Tasks[parentIndex].ChildTasks, childIndex)
		},
		async getSchools () {
			this.schools = await SchoolService.getSchools()
		},
		async getPrograms () {
			this.programs = await SchoolService.getPrograms(this.planning.Info.School.id)
		},
		async getCourses () {
			if (!this.planning.Info.Program) this.courses = []
			else this.courses = await SchoolService.getCourses(this.planning.Info.Program.id)
		},
		async runSetup () {
			await this.getPlanning()
			await this.getSchools()
			if (this.action === 'edit') {
				this.programs = await SchoolService.getPrograms(this.planning.Info.School.id)
				this.courses = await SchoolService.getCourses(this.planning.Info.Program.id)
			} else if (this.action === 'create') {
				if (this.$store.state.authentication.user.School) {
					// Set school
					const schoolId = this.$store.state.authentication.user.School.id
					this.planning.Info.School = this.schools.find(school => school.id === schoolId)
					await this.getPrograms()
					if (this.$store.state.authentication.user.Program) {
						// Set program
						const programId = this.$store.state.authentication.user.Program.id
						this.planning.Info.Program = this.programs.find(program => program.id === programId)
					}
				}
			}
			this.setup = true
		},
		getEditorWeeks () {
			var editorWeeks = DateService.getWeeks(this.planning.Info.start, this.planning.Info.end)
			// Add taskfields
			editorWeeks.forEach(function (week) {
				week.newTask = {}
			})
			this.editorWeeks = editorWeeks
		},
		setStart (day) {
			if (new Date(day.string) > new Date(this.planning.Info.end)) {
				this.$store.dispatch('openPopup', { title: 'Start date', message: 'The end date cannot be before the start date.'	})
				return
			}
			this.planning.Info.start = day.string
		},
		setEnd (day) {
			if (new Date(day.string) < new Date(this.planning.Info.start)) {
				this.$store.dispatch('openPopup', { title: 'End date', message: 'The end date cannot be before the start date.'	})
				return
			}
			this.planning.Info.end = day.string
		},
		setDeadlineDate (index, day) {
			if (index === -1) this.newDeadline.date = day.string
			else this.planning.Deadlines[index].date = day.string
		}
	},
	computed: {
		action () {
			if (this.$route.params.id) return 'edit'
			else return 'create'
		}
	},
	watch: {
		'$route' () {
			this.runSetup()
		},
		'planning.Info.School' () {
			if (this.setup) {
				this.planning.Info.Program = null
				this.getPrograms()
			}
		},
		'planning.Info.Program' () {
			if (this.setup) {
				if (!this.planning.Info.Program) this.courses = []
				else this.getCourses()
				this.planning.Info.Course = null
			}
		},
		'planning.Info.Course' () {
			if (this.setup) {
				if (!this.planning.Info.Course) {
					this.planning.Info.icon = null
					this.planning.Info.title = null
				} else {
					const course = this.planning.Info.Course
					// Check if the user already has a planning for this course
					const plannings = this.$store.state.plannings
					if (plannings) {
						if (plannings.findIndex(planning => planning.Info.Course.id === course.id) >= 0) {
							alert('You already have a planning for this course!')
							this.planning.Info.Course = null
							this.planning.Info.icon = null
							this.planning.Info.title = null
							return
						}
					}
					this.planning.Info.icon = course.icon
					this.planning.Info.title = course.title
				}
			}
		},
		'planning.Info.start' () {
			this.getEditorWeeks()
		},
		'planning.Info.end' () {
			this.getEditorWeeks()
		}
	},
	created () {
		this.runSetup()
		this.getEditorWeeks()
	}
}
</script>

<style lang="scss" scoped>
@import "@/styles/views/_planningeditor.scss";
</style>
